import Image from 'next/image';
import { cn } from '@/lib/utils';

export type LogoVariant = 'full' | 'icon' | 'text' | 'hero';
export type LogoSize = 'sm' | 'md' | 'lg' | 'xl' | 'custom';

export interface LogoProps {
  /**
   * Variant of the logo to display
   * - 'full': Full logo with icon and text (default)
   * - 'icon': Icon only (hexagonal icon)
   * - 'text': Text only ("OPS NEXT LEVEL")
   * - 'hero': Large hero logo for landing/login pages
   */
  variant?: LogoVariant;
  
  /**
   * Size preset for the logo
   * - 'sm': Small (height: 24px)
   * - 'md': Medium (height: 32px)
   * - 'lg': Large (height: 48px)
   * - 'xl': Extra large (height: 64px)
   * - 'custom': Use custom className for sizing
   */
  size?: LogoSize;
  
  /**
   * Custom className for additional styling
   */
  className?: string;
  
  /**
   * Custom className applied directly to the image
   */
  imageClassName?: string;
  
  /**
   * Whether to prioritize loading (for above-the-fold logos)
   */
  priority?: boolean;
  
  /**
   * Alt text for accessibility
   */
  alt?: string;
  
  /**
   * Custom width (only used when size is 'custom')
   */
  width?: number;
  
  /**
   * Custom height (only used when size is 'custom')
   */
  height?: number;
}

const sizeMap: Record<LogoSize, { width: number; height: number; className?: string }> = {
  sm: { width: 120, height: 24 },
  md: { width: 180, height: 32 },
  lg: { width: 240, height: 48 },
  xl: { width: 320, height: 64 },
  custom: { width: 180, height: 40 },
};

const variantImageMap: Record<LogoVariant, string> = {
  full: '/images/logo-full-v1.2.png',
  icon: '/images/logo-icon.png',
  text: '/images/logo-full-v1.2.png', // Using full logo for text variant as fallback
  hero: '/images/OPS_Next_Level_Full_Logo.png', // Large hero logo for login/landing pages
};

/**
 * Logo Component
 * 
 * A reusable logo component for the OPS Next Level system.
 * Supports multiple variants and sizes for consistent branding across the application.
 * 
 * @example
 * ```tsx
 * // Full logo, medium size
 * <Logo variant="full" size="md" />
 * 
 * // Icon only, small size
 * <Logo variant="icon" size="sm" />
 * 
 * // Custom size
 * <Logo variant="full" size="custom" width={200} height={50} />
 * ```
 */
export function Logo({
  variant = 'full',
  size = 'md',
  className,
  imageClassName,
  priority = false,
  alt = 'OPS Next Level',
  width,
  height,
}: LogoProps) {
  const sizeConfig = sizeMap[size];
  const imageSrc = variantImageMap[variant];
  
  // Use custom dimensions if provided, otherwise use size preset
  const finalWidth = size === 'custom' && width ? width : sizeConfig.width;
  const finalHeight = size === 'custom' && height ? height : sizeConfig.height;
  
  // Determine height class based on size
  const heightClass = size === 'custom' 
    ? '' 
    : size === 'sm' 
      ? 'h-6' 
      : size === 'md' 
        ? 'h-8' 
        : size === 'lg' 
          ? 'h-12' 
          : 'h-16';
  
  // For icon variant, use square aspect ratio
  const iconClassName = variant === 'icon' ? 'rounded-xl' : '';
  
  return (
    <div className={cn('inline-flex items-center', className)}>
      <Image
        src={imageSrc}
        alt={alt}
        width={finalWidth}
        height={finalHeight}
        className={cn(
          'w-auto',
          heightClass,
          iconClassName,
          imageClassName
        )}
        priority={priority}
      />
    </div>
  );
}
